import { useCallback, useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import { API_ROUTES } from "../../configuration/RoutesApi"
import { useError } from "../../context/useErrorContext"
import { useLoad } from "../../context/useLoadContext"
import { useEmployerLogged } from "../../context/useEmployeeContext"
import { employeeMemory } from "../../Objects/Memory/Memory"
import type { SignInResponseDto } from "../../Objects/Generics/SignInResponseDto"
import { PostFormRequest } from "../../services/ApiClientService"
import { useGetEmployee } from "../Employee/useGetEmployee"

type GoogleCredentialResponse = {
  credential?: string
}

const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID?.trim()

const googleScriptId = "google-identity-services"

interface UseGoogleLoginOptions {
  onSuccess: () => void
}

export function useGoogleLogin({ onSuccess }: UseGoogleLoginOptions) {
  const { t } = useTranslation("general")

  const { t: tLogin } = useTranslation("login")

  const { setError } = useError()

  const { enableLoad, disableLoad } = useLoad()

  const { setEmployee } = useEmployerLogged()

  const { handleGetEmployee } = useGetEmployee()

  const googleButtonRef = useRef<HTMLDivElement | null>(null)

  const renderedRef = useRef(false)

  const handleGoogleCredential = useCallback(
    async (response: GoogleCredentialResponse) => {
      if (!response.credential) {
        setError(t("label_title_erro"), tLogin("google_token_error"))
        return
      }

      enableLoad()

      try {
        const result = await PostFormRequest<SignInResponseDto>(API_ROUTES.AUTH.LOGIN_GOOGLE, {
          googleIdToken: response.credential,
        })

        if (result.success && result.data?.token) {
          localStorage.setItem(API_ROUTES.TOKEN, JSON.stringify(result.data.token))

          localStorage.setItem(API_ROUTES.EMAIL_KEY, JSON.stringify(result.data.username))

          await handleGetEmployee(() => {})

          setEmployee(employeeMemory.findById(result.data.id) ?? null)

          onSuccess()
          return
        }

        throw new Error(result.message)
      } catch (e: unknown) {
        const message = e instanceof Error ? e.message : t("label_unknown_error_description")

        setError(t("label_title_erro"), message)
      } finally {
        disableLoad()
      }
    },
    [disableLoad, enableLoad, handleGetEmployee, onSuccess, setEmployee, setError, t, tLogin],
  )

  useEffect(() => {
    if (!googleClientId || renderedRef.current) {
      return
    }

    const renderGoogleButton = () => {
      if (!googleButtonRef.current || !window.google?.accounts?.id || renderedRef.current) {
        return
      }

      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: (response) => void handleGoogleCredential(response),
      })

      window.google.accounts.id.renderButton(googleButtonRef.current, {
        theme: "outline",
        size: "large",
        type: "standard",
        shape: "rectangular",
        text: "signin_with",
        width: Math.min(360, Math.max(260, Math.floor(googleButtonRef.current.getBoundingClientRect().width))),
        locale: "pt-BR",
      })

      renderedRef.current = true
    }

    const existingScript = document.getElementById(googleScriptId)

    if (existingScript) {
      renderGoogleButton()
      return
    }

    const script = document.createElement("script")
    script.id = googleScriptId
    script.src = "https://accounts.google.com/gsi/client"
    script.async = true
    script.defer = true
    script.onload = renderGoogleButton
    script.onerror = () => setError(t("label_title_erro"), tLogin("google_script_error"))

    document.head.appendChild(script)
  }, [handleGoogleCredential, setError, t, tLogin])

  return {
    googleButtonRef,
    isGoogleLoginAvailable: Boolean(googleClientId),
  }
}
